import React, { useState, useEffect, useCallback, useMemo } from "react";
import Select from "react-select";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Label,
    Input,
    Button,
    FormGroup,
    Form,
    InputGroup,
    FormFeedback,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { PatternFormat } from "react-number-format";
import Loaders from "components/Common/Loaders";
import {
    urlEndPoint,
    toPhoneDigits,
    US_PHONE_INPUT_FORMAT,
} from "helpers/GeneralHelper";
import { fetchCommonStatusList } from "model/UserModel";
import {
    getCompanyUserById,
    getCompanyUserClients,
    getCompanyUserTypeOptions,
    createCompanyUser,
    updateCompanyUser,
    deleteCompanyUser,
} from "model/CompanyUserModel";
import { getUserTypeById } from "model/UserTypeModel";

import "./company-user-add-edit.scss";

const unwrapArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    const payloadRoot = payload?.data ?? payload;
    if (Array.isArray(payloadRoot)) return payloadRoot;
    const nestedRecords =
        payloadRoot?.records ?? payloadRoot?.rows ?? payloadRoot?.items;
    return Array.isArray(nestedRecords) ? nestedRecords : [];
};

const normalizeId = (value) => String(value ?? "").trim();

const buildUserImageInlineUrl = (userImageUrl = "") => {
    const raw = String(userImageUrl ?? "").trim();
    if (!raw) return "";
    if (/\/inline\/?$/i.test(raw)) return raw.replace(/\/$/, "");
    return `${raw.replace(/\/$/, "")}/inline`;
};

const COMPANY_USER_EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i;

const buildCompanyUserFormSchema = (isEdit = false) => {
    const baseFields = {
        userTypeId: Yup.string().trim().required("User type is required"),
        name: Yup.string().trim().required("Name is required"),
        email: Yup.string()
            .trim()
            .required("Email is required")
            .matches(COMPANY_USER_EMAIL_REGEX, "Enter a valid email"),
        status: Yup.string().trim().required("Status is required"),
    };

    if (isEdit) {
        return Yup.object().shape({
            ...baseFields,
            password: Yup.string().trim(),
            repassword: Yup.string()
                .trim()
                .when("password", {
                    is: (value) => Boolean(String(value || "").trim()),
                    then: (schema) =>
                        schema
                            .required("Re-password is required")
                            .oneOf(
                                [Yup.ref("password")],
                                "Password and re-password do not match"
                            ),
                }),
        });
    }

    return Yup.object().shape({
        ...baseFields,
        password: Yup.string().trim().required("Password is required"),
        repassword: Yup.string()
            .trim()
            .required("Re-password is required")
            .oneOf([Yup.ref("password")], "Password and re-password do not match"),
    });
};

const buildCompanyUserValidationValues = (form = {}) => ({
    userTypeId: normalizeId(form?.userTypeId),
    name: String(form?.name ?? "").trim(),
    email: String(form?.email ?? "").trim(),
    status: String(form?.status ?? "").trim(),
    password: String(form?.password ?? ""),
    repassword: String(form?.repassword ?? ""),
});

/** Map `common/status` payload to `{ value, label }` (value lowercased for consistency with user API). */
const normalizeStatusOptions = (payload) => {
    const list = unwrapArray(payload);
    const mapped = list
        .map((item) => {
            const raw = item?.value ?? item?.id ?? item?.code ?? item?.key ?? "";
            const value = String(raw).trim().toLowerCase();
            if (!value) return null;
            const label =
                item?.label ??
                item?.name ??
                item?.title ??
                item?.display ??
                item?.text ??
                value;
            return { value, label: String(label) };
        })
        .filter(Boolean);
    if (mapped.length) return mapped;
    return [
        { value: "active", label: "Active" },
        { value: "deactive", label: "Deactive" },
        { value: "blocked", label: "Blocked" },
    ];
};

/** Same as `CandidateHireStatusAddEditModal` — `className="select2-selection"` + portal z-index. */
const selectZIndexStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const parseIdList = (value) => {
    if (Array.isArray(value)) {
        return value.map(normalizeId).filter(Boolean);
    }
    return String(value ?? "")
        .split(",")
        .map((part) => normalizeId(part))
        .filter(Boolean);
};

const normalizeUserTypeName = (value = "") =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const mapCompanyUserTypeOptions = (rows = []) =>
    (Array.isArray(rows) ? rows : [])
        .map((row = {}) => ({
            id: normalizeId(row?.id ?? row?._id ?? row?.value),
            name: String(
                row?.display ?? row?.name ?? row?.title ?? row?.label ?? ""
            ).trim(),
            status: normalizeUserTypeName(row?.status),
            uniqueClient: Boolean(row?.uniqueClient),
        }))
        .filter(
            (row) =>
                row.id &&
                (!row.status || row.status === "active")
        );

const resolveCompanyUserSubmitTypeId = (userTypeId = "") => normalizeId(userTypeId);

const mapFetchedUserTypeForSelect = (detail = {}) =>
    normalizeId(detail.userTypeId ?? detail.typeId);

const appendCompanyUserFormDataValue = (formDataBody, key, value) => {
    if (value === undefined || value === null) {
        formDataBody.append(key, "");
        return;
    }
    formDataBody.append(key, value);
};

const isSuccessResponse = (res) =>
    res?.success !== false && (res?.success || res?.isSuccess || res?.data != null);

/** Build `multipart/form-data` for `POST/PUT user/company/{companyId}/user`. */
const buildCompanyUserFormData = (formData) => {
    const typeId = resolveCompanyUserSubmitTypeId(formData.userTypeId);
    const teamId = (formData.teamIds || []).map(normalizeId).filter(Boolean).join(",");
    const companyIds = (formData.companyIds || []).map(String).join(",");
    const clientAccess = (formData.clientIds || []).map(String).join(",");
    const password = String(formData.password || "").trim();
    const fd = new FormData();

    appendCompanyUserFormDataValue(fd, "Name", String(formData.name || "").trim());
    appendCompanyUserFormDataValue(fd, "Email", String(formData.email || "").trim());
    appendCompanyUserFormDataValue(fd, "TypeId", String(typeId ?? ""));
    appendCompanyUserFormDataValue(fd, "TeamId", teamId);
    appendCompanyUserFormDataValue(fd, "Phone", toPhoneDigits(formData.phone || "").trim());
    appendCompanyUserFormDataValue(fd, "PhoneExt", String(formData.phoneExt || "").trim());
    appendCompanyUserFormDataValue(fd, "Status", String(formData.status || "").trim());
    appendCompanyUserFormDataValue(fd, "WorkShiftId", String(formData.workShiftId || "").trim());
    appendCompanyUserFormDataValue(fd, "Layout", String(formData.layout || "").trim());
    appendCompanyUserFormDataValue(fd, "Zone", String(formData.zone || "").trim());
    appendCompanyUserFormDataValue(fd, "Password", password);
    appendCompanyUserFormDataValue(fd, "CompanyIds", companyIds);
    appendCompanyUserFormDataValue(fd, "ClientAccess", clientAccess);
    appendCompanyUserFormDataValue(fd, "MaskName", String(formData.maskName || "").trim());
    appendCompanyUserFormDataValue(fd, "AboutUser", String(formData.about || "").trim());

    if (formData.profileFile instanceof File) {
        fd.append("UserImageFile", formData.profileFile);
    } else {
        appendCompanyUserFormDataValue(fd, "UserImageFile", "");
    }

    return fd;
};

const CompanyUserAddEdit = () => {
    const { companyId: companyIdParam, userId } = useParams();
    const companyId = normalizeId(companyIdParam);
    const navigate = useNavigate();
    const isEditMode = Boolean(normalizeId(userId));

    const [loadingPage, setLoadingPage] = useState(!!isEditMode);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepassword, setShowRepassword] = useState(false);
    const [statusOptions, setStatusOptions] = useState([]);
    const [userTypes, setUserTypes] = useState([]);
    const [clientOptions, setClientOptions] = useState([]);
    const [multipleClient, setMultipleClient] = useState(true);
    const [errors, setErrors] = useState({});

    const userTypeSelectOptions = useMemo(
        () =>
            userTypes.map((item) => ({
                value: item.id,
                label: item.name || item.id,
            })),
        [userTypes]
    );

    const companyUserFormSchema = useMemo(
        () => buildCompanyUserFormSchema(isEditMode),
        [isEditMode]
    );

    const [formData, setFormData] = useState({
        id: "",
        userTypeId: "",
        workShiftId: "",
        name: "",
        email: "",
        password: "",
        repassword: "",
        maskName: "",
        phone: "",
        phoneExt: "",
        about: "",
        teamIds: [],
        status: "active",
        typeLabel: "",
        blocked: false,
        showClient: false,
        companyIds: [],
        clientIds: [],
        profileImage: "",
        profileImageUrl: "",
        profileFile: null,
        layout: "",
        zone: "",
    });

    useEffect(() => {
        if (!companyId) {
            toast.error("Company id is required in the URL");
            navigate(`/${urlEndPoint.company}`);
        }
    }, [companyId, navigate]);

    useEffect(() => {
        if (!isEditMode && companyId) {
            setFormData((previousForm) => ({
                ...previousForm,
                companyIds:
                    Array.isArray(previousForm.companyIds) &&
                    previousForm.companyIds.length > 0
                        ? previousForm.companyIds
                        : [companyId],
            }));
        }
    }, [isEditMode, companyId]);

    const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
    useEffect(() => {
        if (!(formData.profileFile instanceof File)) {
            setAvatarPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(formData.profileFile);
        setAvatarPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [formData.profileFile]);

    const displayAvatarUrl = useMemo(() => {
        if (avatarPreviewUrl) return avatarPreviewUrl;
        return buildUserImageInlineUrl(formData.profileImageUrl);
    }, [avatarPreviewUrl, formData.profileImageUrl]);

    const clearFieldError = (fieldName) => {
        if (!errors[fieldName]) return;
        setErrors((previousErrors) => ({ ...previousErrors, [fieldName]: "" }));
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        if (type === "checkbox") {
            setFormData((previousForm) => ({
                ...previousForm,
                [name]: checked,
            }));
        } else {
            setFormData((previousForm) => ({ ...previousForm, [name]: value }));
        }
        if (companyUserFormSchema.fields[name]) {
            clearFieldError(name);
        }
    };

    const handlePhoneValueChange = (values) => {
        const nextValue = toPhoneDigits(values?.value || "");
        setFormData((previousForm) => ({
            ...previousForm,
            phone: nextValue,
        }));
    };

    const applyUserTypeClientRule = useCallback(async (userTypeId) => {
        const normalizedUserTypeId = normalizeId(userTypeId);
        if (!normalizedUserTypeId) {
            setMultipleClient(true);
            return;
        }

        const matchedType = userTypes.find((item) => item.id === normalizedUserTypeId);
        if (matchedType) {
            setMultipleClient(!matchedType.uniqueClient);
            return;
        }

        // const userTypeApiResponse = await getUserTypeById(normalizedUserTypeId);
        // const userTypePayload = userTypeApiResponse?.data ?? userTypeApiResponse;
        // setMultipleClient(!Boolean(userTypePayload?.uniqueClient));
    }, [userTypes]);

    const loadAssignClients = async () => {
        if (!companyId) {
            setClientOptions([]);
            return;
        }
        const response = await getCompanyUserClients({ companyId });
        const clientData = response?.data;
        if (response) {
            setClientOptions(
                (Array.isArray(clientData) ? clientData : [])
                    .map((row) => ({
                        id: normalizeId(row?.id ?? row?._id),
                        name: String(row?.display ?? row?.name ?? row?.id ?? "").trim(),
                    }))
                    .filter((row) => row.id)
            );
        } else {
            setClientOptions([]);
        }
    };

    const loadReferenceData = useCallback(async () => {
        setLoadingOptions(true);
        try {
            const [userTypesApiResponse, accountStatusesApiResponse] =
                await Promise.all([
                    getCompanyUserTypeOptions(),
                    fetchCommonStatusList(),
                ]);

            const userTypeRows = unwrapArray(
                userTypesApiResponse?.data ?? userTypesApiResponse
            );
            setUserTypes(mapCompanyUserTypeOptions(userTypeRows));

            setStatusOptions(
                normalizeStatusOptions(
                    accountStatusesApiResponse?.data ?? accountStatusesApiResponse
                )
            );
        } catch (loadError) {
            toast.error(loadError?.message || "Unable to load form options");
        } finally {
            setLoadingOptions(false);
        }
    }, [userId, companyId]);

    const loadUser = useCallback(async () => {
        const id = normalizeId(userId);
        if (!id) return;
        setLoadingPage(true);
        try {
            const companyUserApiResponse = await getCompanyUserById(id);
            const companyUserDetail =
                companyUserApiResponse?.data ?? companyUserApiResponse;
            if (!companyUserDetail || typeof companyUserDetail !== "object") {
                toast.error(companyUserApiResponse?.message || "User not found");
                return;
            }

            const teamIdsRaw =
                companyUserDetail.teamIds ?? companyUserDetail.teamId ?? "";
            const teamIds = Array.isArray(teamIdsRaw)
                ? teamIdsRaw.map((tid) => normalizeId(tid)).filter(Boolean)
                : String(teamIdsRaw || "")
                    .split(",")
                    .map((tid) => normalizeId(tid))
                    .filter(Boolean);

            setFormData((previousForm) => ({
                ...previousForm,
                id: normalizeId(companyUserDetail.id ?? companyUserDetail.userId) ||
                    id,
                userTypeId: mapFetchedUserTypeForSelect(companyUserDetail),
                workShiftId:
                    normalizeId(
                        companyUserDetail.workShiftId ?? companyUserDetail.shift
                    ) || "",
                name: companyUserDetail.userName ?? companyUserDetail.name ?? "",
                email: companyUserDetail.userEmail ?? companyUserDetail.email ?? "",
                maskName: companyUserDetail.maskName ?? "",
                phone: toPhoneDigits(companyUserDetail.phone ?? ""),
                phoneExt:
                    companyUserDetail.callPhoneExt ??
                    companyUserDetail.phoneExt ??
                    "",
                about: companyUserDetail.aboutUser ?? companyUserDetail.about ?? "",
                teamIds,
                status: String(
                    companyUserDetail.userStatus ??
                        companyUserDetail.status ??
                        "active"
                ).toLowerCase(),
                typeLabel: companyUserDetail.type ?? "",
                blocked: Boolean(companyUserDetail.blocked),
                showClient: Boolean(companyUserDetail.showClient),
                companyIds: parseIdList(
                    companyUserDetail.companyIds ?? companyUserDetail.companyId
                ),
                clientIds: parseIdList(
                    companyUserDetail.clientIds ??
                        companyUserDetail.clientAccess
                ),
                profileImage:
                    companyUserDetail.profileImage ??
                    companyUserDetail.userImageId ??
                    "",
                profileImageUrl: String(companyUserDetail.userImageUrl ?? "").trim(),
                profileFile: null,
                layout: companyUserDetail.layout ?? "",
                zone: companyUserDetail.zone ?? "",
                password: "",
                repassword: "",
            }));

            const loadedUserTypeId = mapFetchedUserTypeForSelect(companyUserDetail);
            const loadedTypeLabel = String(
                companyUserDetail.type ??
                    companyUserDetail.userType ??
                    companyUserDetail.typeName ??
                    ""
            ).trim();
            if (loadedUserTypeId && loadedTypeLabel) {
                setUserTypes((previousTypes) => {
                    if (previousTypes.some((item) => item.id === loadedUserTypeId)) {
                        return previousTypes;
                    }
                    return [
                        ...previousTypes,
                        {
                            id: loadedUserTypeId,
                            name: loadedTypeLabel,
                            uniqueClient: false,
                        },
                    ];
                });
            }
            if (loadedUserTypeId) {
                await applyUserTypeClientRule(loadedUserTypeId);
            }
        } catch (loadUserError) {
            toast.error(loadUserError?.message || "Unable to load user");
        } finally {
            setLoadingPage(false);
        }
    }, [userId, companyId, applyUserTypeClientRule]);

    useEffect(() => {
        loadReferenceData();
    }, [loadReferenceData]);

    useEffect(() => {
        if (isEditMode) {
            loadUser();
        } else {
            setLoadingPage(false);
        }
    }, [isEditMode, loadUser]);

    useEffect(() => {
        if (!companyId) return;
        if (isEditMode && loadingPage) return;
        loadAssignClients();
    }, [companyId, isEditMode, loadingPage]);

    const onUserTypeSelectChange = async (selectedOption) => {
        const raw = selectedOption?.value;
        const value =
            raw === undefined || raw === null || raw === ""
                ? ""
                : normalizeId(raw);
        setFormData((previousForm) => ({
            ...previousForm,
            userTypeId: value,
            typeLabel: selectedOption?.label ?? "",
        }));
        clearFieldError("userTypeId");
        await applyUserTypeClientRule(value);
    };

    const updateFormSelectField = (fieldName, selectedOption) => {
        setFormData((previousForm) => ({
            ...previousForm,
            [fieldName]: selectedOption ? String(selectedOption.value) : "",
        }));
        if (companyUserFormSchema.fields[fieldName]) {
            clearFieldError(fieldName);
        }
    };

    const setClientSelection = (clientId, isChecked) => {
        const normalizedClientId = normalizeId(clientId);
        setFormData((previousForm) => {
            if (!multipleClient) {
                return {
                    ...previousForm,
                    clientIds:
                        isChecked && normalizedClientId ? [normalizedClientId] : [],
                };
            }
            const nextClientIdSet = new Set(
                previousForm.clientIds.map(normalizeId)
            );
            if (isChecked && normalizedClientId) {
                nextClientIdSet.add(normalizedClientId);
            } else {
                nextClientIdSet.delete(normalizedClientId);
            }
            return {
                ...previousForm,
                clientIds: Array.from(nextClientIdSet),
            };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            await companyUserFormSchema.validate(
                buildCompanyUserValidationValues(formData),
                { abortEarly: false }
            );
            setErrors({});
        } catch (validationError) {
            const validationErrors = {};
            validationError?.inner?.forEach((errorItem) => {
                if (errorItem?.path && !validationErrors[errorItem.path]) {
                    validationErrors[errorItem.path] = errorItem.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        if (!companyId) {
            toast.error("Company id is required");
            return;
        }

        const formBody = buildCompanyUserFormData(formData);
        setSaving(true);
        try {
            const res = isEditMode
                ? await updateCompanyUser(companyId, normalizeId(userId), formBody)
                : await createCompanyUser(companyId, formBody);

            if (!isSuccessResponse(res)) {
                toast.error(res?.message || res?.error?.message);
                return;
            }

            toast.success(res?.message || (isEditMode ? "User updated" : "User created"));
            navigate(`/${urlEndPoint.companyUser}/${encodeURIComponent(companyId)}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!isEditMode || !normalizeId(userId) || !companyId) return;
        const swalResult = await Swal.fire({
            title: "Remove company user?",
            text: "This will remove the user from this company.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!swalResult?.isConfirmed) return;
        setSaving(true);
        try {
            const res = await deleteCompanyUser(normalizeId(userId), companyId);
            if (res?.success !== false && (res?.success || res?.isSuccess || res?.data != null)) {
                toast.success(res?.message || "Deleted");
                navigate(`/${urlEndPoint.companyUser}/${encodeURIComponent(companyId)}`);
                return;
            }
            toast.error(res?.message || "Unable to delete");
        } finally {
            setSaving(false);
        }
    };

    const showLoader = loadingOptions || (isEditMode && loadingPage);

    return (
        <div className="page-content manage-user-add-edit">
            <Container fluid className="manage-user-add-edit__container">
                <Card className="manage-user-add-edit__card manage-user-add-edit-page border-0 mb-4">
                    <CardBody className="manage-user-add-edit__card-body overflow-visible">
                        <header className="manage-user-add-edit__hero">
                            <div className="manage-user-add-edit__title-block">
                                {/* <p className="manage-user-add-edit__eyebrow mb-0">
                                    {isEditMode ? "Company user" : "New company user"}
                                </p> */}
                                <h1 className="manage-user-add-edit__title">
                                    {isEditMode ? "Edit company user" : "Add company user"}
                                </h1>
                                {/* <p className="manage-user-add-edit__subtitle">
                                    {isEditMode
                                        ? "Review teams, access, and credentials — then save."
                                        : "Fill in account details, assign teams, and set a password."}
                                </p> */}
                            </div>
                            <Button
                                type="button"
                                color="light"
                                className="manage-user-add-edit__back-btn"
                                onClick={() =>
                                    companyId
                                        ? navigate(`/${urlEndPoint.companyUser}/${encodeURIComponent(companyId)}`)
                                        : navigate(`/${urlEndPoint.company}`)
                                }
                            >
                                <i className="bx bx-arrow-back me-1 align-middle" /> Back to list
                            </Button>
                        </header>

                        {showLoader ? (
                            <Loaders />
                        ) : (
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4 g-lg-5">
                                    <Col lg={6}>
                                        <div className="manage-user-add-edit__panel">
                                            <p className="manage-user-add-edit__section-title mb-3">
                                                Photo &amp; profile
                                            </p>
                                            <FormGroup className="mb-4 text-center">
                                                <div className="manage-user-add-edit__avatar-wrap">
                                                    <div className="manage-user-add-edit__avatar-ring">
                                                        <div className="manage-user-add-edit__avatar-inner">
                                                            {displayAvatarUrl ? (
                                                                <img
                                                                    src={displayAvatarUrl}
                                                                    alt={formData.name || "User"}
                                                                />
                                                            ) : (
                                                                <i className="fa fa-user" aria-hidden />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <label
                                                        htmlFor="fileInput"
                                                        className="manage-user-add-edit__avatar-fab mb-0"
                                                    >
                                                        <i className="fa fa-camera" aria-hidden />
                                                    </label>
                                                    <input
                                                        id="fileInput"
                                                        type="file"
                                                        accept="image/*"
                                                        className="d-none"
                                                        onChange={(event) => {
                                                            const selectedFile =
                                                                event.target.files?.[0];
                                                            if (selectedFile) {
                                                                setFormData((previousForm) => ({
                                                                    ...previousForm,
                                                                    profileFile: selectedFile,
                                                                    profileImage: selectedFile.name,
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <p className="manage-user-add-edit__hint text-center mb-0">
                                                    JPG or PNG, recommended square image.
                                                </p>
                                            </FormGroup>

                                            <p className="manage-user-add-edit__section-title">
                                                Account details
                                            </p>
                                        <FormGroup className="mb-3">
                                            <Label>
                                                User type <span className="text-danger">*</span>
                                            </Label>
                                            <div className={errors.userTypeId ? "is-invalid" : ""}>
                                                <Select
                                                    options={userTypeSelectOptions}
                                                    className={`select2-selection${errors.userTypeId ? " is-invalid" : ""}`}
                                                    placeholder="Select user type"
                                                    value={
                                                        userTypeSelectOptions.find(
                                                            (opt) =>
                                                                String(opt.value) ===
                                                                String(formData?.userTypeId || "")
                                                        ) || null
                                                    }
                                                    onChange={onUserTypeSelectChange}
                                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                    styles={selectZIndexStyles}
                                                    isClearable={false}
                                                />
                                            </div>
                                            {errors.userTypeId ? (
                                                <div className="invalid-feedback d-block">
                                                    {errors.userTypeId}
                                                </div>
                                            ) : null}
                                        </FormGroup>

                                        {formData.typeLabel ? (
                                            <div className="mb-3">
                                                <span className="manage-user-add-edit__role-pill">
                                                    <i className="mdi mdi-shield-account-outline" aria-hidden />
                                                    {formData.typeLabel}
                                                </span>
                                            </div>
                                        ) : null}

                                        <FormGroup className="mb-3">
                                            <Label>
                                                Name <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                invalid={!!errors.name}
                                            />
                                            <FormFeedback>{errors.name}</FormFeedback>
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <Label>
                                                Email <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                invalid={!!errors.email}
                                                readOnly={isEditMode}
                                                style={
                                                    isEditMode
                                                        ? { backgroundColor: "#f1f5f9", cursor: "not-allowed" }
                                                        : undefined
                                                }
                                            />
                                            <FormFeedback>{errors.email}</FormFeedback>
                                            {isEditMode ? (
                                                <small className="manage-user-add-edit__hint">
                                                    Email cannot be changed for this account.
                                                </small>
                                            ) : null}
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <Label>
                                                Status <span className="text-danger">*</span>
                                            </Label>
                                            <div className={errors.status ? "is-invalid" : ""}>
                                                <Select
                                                    options={statusOptions}
                                                    className={`select2-selection${errors.status ? " is-invalid" : ""}`}
                                                    placeholder="Select status"
                                                    value={
                                                        statusOptions.find(
                                                            (opt) =>
                                                                String(opt.value) ===
                                                                String(formData?.status || "")
                                                        ) || null
                                                    }
                                                    onChange={(selectedOption) =>
                                                        updateFormSelectField("status", selectedOption)
                                                    }
                                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                    styles={selectZIndexStyles}
                                                    isClearable={false}
                                                />
                                            </div>
                                            {errors.status ? (
                                                <div className="invalid-feedback d-block">
                                                    {errors.status}
                                                </div>
                                            ) : null}
                                        </FormGroup>

                                        <FormGroup className="mb-3">
                                            <Label>About</Label>
                                            <Input
                                                type="textarea"
                                                rows="4"
                                                name="about"
                                                value={formData.about}
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                        </div>
                                    </Col>

                                    <Col lg={6}>
                                        <div className="manage-user-add-edit__panel">
                                            <p className="manage-user-add-edit__section-title">
                                                Assign client
                                            </p>
                                        <div className="manage-user-add-edit__teams mb-4">
                                            <h5 className="manage-user-add-edit__teams-title">
                                                <i className="mdi mdi-briefcase-account-outline" aria-hidden />
                                                Assign client
                                            </h5>
                                            {clientOptions.length === 0 ? (
                                                <p className="text-muted small mb-0">
                                                    No clients available.
                                                </p>
                                            ) : (
                                                <Row>
                                                    {clientOptions.map((client) => {
                                                        const selected = formData.clientIds
                                                            .map(normalizeId)
                                                            .includes(client.id);
                                                        const clientIdsKey = [
                                                            ...formData.clientIds,
                                                        ]
                                                            .map(normalizeId)
                                                            .sort()
                                                            .join("|");
                                                        return (
                                                            <Col
                                                                md={6}
                                                                key={client.id}
                                                                className="manage-user-add-edit__team-item"
                                                            >
                                                                <FormGroup check>
                                                                    {multipleClient ? (
                                                                        <Input
                                                                            key={`client-cb-${client.id}-${clientIdsKey}`}
                                                                            type="checkbox"
                                                                            defaultChecked={selected}
                                                                            onChange={(e) =>
                                                                                setClientSelection(
                                                                                    client.id,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <Input
                                                                            key={`client-r-${client.id}-${formData.clientIds[0] ?? "x"}`}
                                                                            type="radio"
                                                                            name="singleClient"
                                                                            value={client.id}
                                                                            defaultChecked={
                                                                                normalizeId(
                                                                                    formData.clientIds[0]
                                                                                ) === client.id
                                                                            }
                                                                            onChange={() =>
                                                                                setFormData((previousForm) => ({
                                                                                    ...previousForm,
                                                                                    clientIds: [client.id],
                                                                                }))
                                                                            }
                                                                        />
                                                                    )}
                                                                    <Label check className="ms-1">
                                                                        {client.name || client.id}
                                                                    </Label>
                                                                </FormGroup>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            )}
                                        </div>

                                        <p className="manage-user-add-edit__section-title">
                                            Work &amp; contact
                                        </p>
                                        <FormGroup className="mb-3">
                                            <Label>Mask name</Label>
                                            <Input
                                                type="text"
                                                name="maskName"
                                                value={formData.maskName}
                                                onChange={handleChange}
                                            />
                                        </FormGroup>

                                        {/* <FormGroup className="mb-3">
                                            <Label>Work shift</Label>
                                            <Select
                                                options={workShiftSelectOptions}
                                                className="select2-selection"
                                                placeholder="Select shift"
                                                value={
                                                    workShiftSelectOptions.find(
                                                        (opt) =>
                                                            String(opt.value) ===
                                                            String(formData?.workShiftId || "")
                                                    ) || null
                                                }
                                                onChange={(selectedOption) =>
                                                    updateFormSelectField("workShiftId", selectedOption)
                                                }
                                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                styles={selectZIndexStyles}
                                                isClearable
                                            />
                                        </FormGroup> */}

                                        <Row>
                                            <Col md={12}>
                                                <FormGroup className="mb-3">
                                                    <Label>Phone</Label>
                                                    <PatternFormat
                                                        customInput={Input}
                                                        format={US_PHONE_INPUT_FORMAT}
                                                        allowEmptyFormatting
                                                        name="phone"
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={formData.phone || ""}
                                                        onValueChange={handlePhoneValueChange}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            {/* <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>Phone ext.</Label>
                                                    <Input
                                                        type="text"
                                                        name="phoneExt"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        value={formData.phoneExt}
                                                        onChange={handlePhoneFieldChange}
                                                    />
                                                </FormGroup>
                                            </Col> */}
                                        </Row>

                                        {/* {isEditMode &&
                                        (formData.companyIds.length > 0 || formData.clientIds.length > 0) ? (
                                            <div className="manage-user-add-edit__meta-chips mb-3">
                                                {formData.companyIds.length > 0 ? (
                                                    <span className="badge bg-light text-dark border">
                                                        Company: {formData.companyIds.join(", ")}
                                                    </span>
                                                ) : null}
                                                {formData.clientIds.length > 0 ? (
                                                    <span className="badge bg-light text-dark border">
                                                        Clients: {formData.clientIds.join(", ")}
                                                    </span>
                                                ) : null}
                                            </div>
                                        ) : null} */}

                                        <p className="manage-user-add-edit__security-label">Security</p>
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>
                                                        {isEditMode ? (
                                                            "Password (leave blank to keep)"
                                                        ) : (
                                                            <>
                                                                Password{" "}
                                                                <span className="text-danger">*</span>
                                                            </>
                                                        )}
                                                    </Label>
                                                    <InputGroup
                                                        className={
                                                            errors.password ? "has-validation" : ""
                                                        }
                                                    >
                                                        <Input
                                                            type={showPassword ? "text" : "password"}
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                            autoComplete="new-password"
                                                            invalid={!!errors.password}
                                                        />
                                                        <Button
                                                            type="button"
                                                            color="light"
                                                            className="border px-2"
                                                            onClick={() => setShowPassword((v) => !v)}
                                                            aria-label={
                                                                showPassword ? "Hide password" : "Show password"
                                                            }
                                                            tabIndex={-1}
                                                        >
                                                            <i
                                                                className={`mdi font-size-18 ${
                                                                    showPassword
                                                                        ? "mdi-eye-off-outline"
                                                                        : "mdi-eye-outline"
                                                                }`}
                                                            />
                                                        </Button>
                                                    </InputGroup>
                                                    <FormFeedback>{errors.password}</FormFeedback>
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup className="mb-3">
                                                    <Label>
                                                        {isEditMode ? (
                                                            "Re-enter password"
                                                        ) : (
                                                            <>
                                                                Re-password{" "}
                                                                <span className="text-danger">*</span>
                                                            </>
                                                        )}
                                                    </Label>
                                                    <InputGroup
                                                        className={
                                                            errors.repassword ? "has-validation" : ""
                                                        }
                                                    >
                                                        <Input
                                                            type={showRepassword ? "text" : "password"}
                                                            name="repassword"
                                                            value={formData.repassword}
                                                            onChange={handleChange}
                                                            autoComplete="new-password"
                                                            invalid={!!errors.repassword}
                                                        />
                                                        <Button
                                                            type="button"
                                                            color="light"
                                                            className="border px-2"
                                                            onClick={() => setShowRepassword((v) => !v)}
                                                            aria-label={
                                                                showRepassword
                                                                    ? "Hide re-password"
                                                                    : "Show re-password"
                                                            }
                                                            tabIndex={-1}
                                                        >
                                                            <i
                                                                className={`mdi font-size-18 ${
                                                                    showRepassword
                                                                        ? "mdi-eye-off-outline"
                                                                        : "mdi-eye-outline"
                                                                }`}
                                                            />
                                                        </Button>
                                                    </InputGroup>
                                                    <FormFeedback>{errors.repassword}</FormFeedback>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="manage-user-add-edit__footer d-flex flex-wrap align-items-center justify-content-end gap-2">
                                    {/* {isEditMode ? (
                                        <Button
                                            type="button"
                                            color="danger"
                                            outline
                                            className="me-auto"
                                            disabled={saving}
                                            onClick={handleDelete}
                                        >
                                            Delete
                                        </Button>
                                    ) : null} */}
                                    <Button
                                        type="button"
                                        color="light"
                                        className="manage-user-add-edit__back-btn"
                                        onClick={() =>
                                            companyId
                                                ? navigate(
                                                      `/${urlEndPoint.companyUser}/${encodeURIComponent(companyId)}`
                                                  )
                                                : navigate(`/${urlEndPoint.company}`)
                                        }
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        className="manage-user-add-edit__submit"
                                        disabled={saving}
                                    >
                                        {saving ? "Saving…" : isEditMode ? "Save changes" : "Create user"}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default CompanyUserAddEdit;
